import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { calculateOrder, createOrderAndGetPaymentUrl, formOrder } from './actions';
import { PROMOCODE_STATUSES } from '../consts/promocode.const';
import type { CalculateOrderResponse } from '../models/calculation.types';
import { CALCULATION_REASONS } from '../consts/calculation.const';

interface CheckoutState {
  promoCode: {
    value: string;
    appliedValue: string | null;
    isValid: boolean | null;
    isLoading: boolean;
    error: string | null;
  };
  calculation: {
    isLoading: boolean;
    error: string | null;
    response: CalculateOrderResponse | null;
    insurance: boolean | null;
    currentRequestId: string | null;
  };
  order: {
    orderId: number | null;
    isLoading: boolean;
    error: string | null;
    paymentUrl: string | null;
    validDateTime: string | null;
  };
}

const initialState: CheckoutState = {
  promoCode: {
    value: '',
    appliedValue: null,
    isValid: null,
    isLoading: false,
    error: null,
  },
  calculation: {
    isLoading: false,
    error: null,
    response: null,
    insurance: null,
    currentRequestId: null,
  },
  order: {
    orderId: null,
    isLoading: false,
    error: null,
    paymentUrl: null,
    validDateTime: null,
  },
};

export const checkoutSlice = createSlice({
  name: 'checkout',
  initialState,
  reducers: {
    setPromoCode: (state, action: PayloadAction<string>) => {
      state.promoCode.value = action.payload;
      state.promoCode.isValid =
        action.payload.trim() === state.promoCode.appliedValue ? true : null;
      state.promoCode.isLoading = false;
      state.promoCode.error = null;
    },
    restoreAppliedPromoCode: (state, action: PayloadAction<string>) => {
      state.promoCode.value = action.payload;
      state.promoCode.appliedValue = action.payload;
      state.promoCode.isValid = true;
      state.promoCode.error = null;
    },
    resetPromoCode: state => {
      state.promoCode = initialState.promoCode;
    },
    resetCalculation: state => {
      state.calculation = initialState.calculation;
    },
    resetOrder: state => {
      state.order = initialState.order;
    },
  },
  extraReducers: builder => {
    builder.addCase(calculateOrder.pending, (state, { meta }) => {
      state.calculation.isLoading = true;
      state.calculation.error = null;
      state.calculation.currentRequestId = meta.requestId;

      if (
        meta.arg.reason === CALCULATION_REASONS.APPLY_PROMO ||
        meta.arg.reason === CALCULATION_REASONS.REMOVE_PROMO
      ) {
        state.promoCode.isLoading = true;
        state.promoCode.error = null;
      }
    });
    builder.addCase(calculateOrder.fulfilled, (state, { payload, meta }) => {
      if (state.calculation.currentRequestId !== meta.requestId) {
        return;
      }

      state.calculation.isLoading = false;
      state.calculation.response = payload;
      state.calculation.insurance = meta.arg.insurance;
      state.calculation.currentRequestId = null;

      if (
        meta.arg.reason === CALCULATION_REASONS.INITIAL ||
        meta.arg.reason === CALCULATION_REASONS.TOGGLE_INSURANCE
      ) {
        return;
      }

      state.promoCode.isLoading = false;

      if (meta.arg.reason === CALCULATION_REASONS.REMOVE_PROMO) {
        state.promoCode.appliedValue = null;
        state.promoCode.isValid = null;
        state.promoCode.error = null;
        return;
      }

      const fallbackError =
        payload.promoCodeStatus === PROMOCODE_STATUSES.NOT_FOUND
          ? 'Промокод не найден'
          : 'Промокод недоступен';

      state.promoCode.isValid = payload.promoCodeStatus === PROMOCODE_STATUSES.ALLOWED;
      state.promoCode.appliedValue =
        payload.promoCodeStatus === PROMOCODE_STATUSES.ALLOWED ? meta.arg.promoCode : null;
      state.promoCode.error =
        payload.promoCodeStatus === PROMOCODE_STATUSES.ALLOWED
          ? null
          : payload.statusDescription || fallbackError;
    });
    builder.addCase(calculateOrder.rejected, (state, { payload, meta }) => {
      if (state.calculation.currentRequestId !== meta.requestId) {
        return;
      }

      state.calculation.isLoading = false;
      state.calculation.error = payload as string;
      state.calculation.currentRequestId = null;

      if (meta.arg.reason === CALCULATION_REASONS.APPLY_PROMO) {
        state.promoCode.isLoading = false;
        state.promoCode.isValid = state.promoCode.appliedValue ? true : false;
        state.promoCode.error = payload as string;
      }

      if (meta.arg.reason === CALCULATION_REASONS.REMOVE_PROMO) {
        state.promoCode.isLoading = false;
      }
    });

    // Обработчики для formOrder
    builder.addCase(formOrder.pending, state => {
      state.order.isLoading = true;
      state.order.error = null;
    });
    builder.addCase(formOrder.fulfilled, (state, { payload }) => {
      state.order.isLoading = false;
      state.order.orderId = payload.orderId;
      state.order.validDateTime = payload.validDateTime;
    });
    builder.addCase(formOrder.rejected, (state, { payload }) => {
      state.order.isLoading = false;
      state.order.error = payload as string;
    });

    // Обработчики для createOrderAndGetPaymentUrl
    builder.addCase(createOrderAndGetPaymentUrl.pending, state => {
      state.order.isLoading = true;
      state.order.error = null;
      state.order.paymentUrl = null;
    });
    builder.addCase(createOrderAndGetPaymentUrl.fulfilled, (state, { payload }) => {
      state.order.isLoading = false;
      state.order.paymentUrl = payload;
    });
    builder.addCase(createOrderAndGetPaymentUrl.rejected, (state, { payload }) => {
      state.order.isLoading = false;
      state.order.error = payload as string;
    });
  },
});

export const {
  setPromoCode,
  restoreAppliedPromoCode,
  resetPromoCode,
  resetCalculation,
  resetOrder,
} = checkoutSlice.actions;
export default checkoutSlice.reducer;
